import { useState, useEffect } from "react"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import {
  TimePicker,
  Select,
  Switch,
  Card,
  Typography,
  Button,
  Input,
  Space,
  Row,
  Col,
  theme,
  notification,
} from "antd"
import "remixicon/fonts/remixicon.css"

import { DAYS } from "../../../data/configs/constants"
import Notify, { notify } from "../Notify"

dayjs.extend(utc)
dayjs.extend(timezone)

const { Text, Title } = Typography

const DEFAULT_VALUE = {
  enable: false,
  shiftStart: dayjs().startOf("day").valueOf(),
  shiftEnd: dayjs().endOf("day").valueOf(),
  selectedDays: [0, 1, 2, 3, 4, 5, 6],
  timezone: dayjs.tz.guess(),
  text: "We are currently unavailable to take your chat request. Please contact us during working hours from Monday to Sunday between 7:00 AM to 12:00 AM.",
}

const OperationalHourSettings = ({ data, onChange }) => {
  const { token: themeConfig } = theme.useToken()
  const [updatedData, setUpdatedData] = useState({ ...DEFAULT_VALUE, ...data })
  const [error, setError] = useState(false)
  const [timeError, setTimeError] = useState(false)

  useEffect(() => {
    setUpdatedData({ ...DEFAULT_VALUE, ...data })
  }, [data])

  const handleSwitch = (checked) => {
    if (checked) {
      setUpdatedData({
        ...updatedData,
        enable: true,
      })
    } else {
      onChange({
        operationalHourSettings: {
          ...data,
          enable: false,
        },
      })
    }
  }

  const handleInputChange = (changedValues) => {
    if (changedValues?.text) {
      setError(false)
    }
    setUpdatedData({
      ...updatedData,
      ...changedValues,
    })
  }

  const handleSave = () => {
    if (
      isNaN(updatedData.shiftStart) ||
      isNaN(updatedData.shiftEnd) ||
      timeError
    ) {
      notify.error({
        message:
          "Selected Time is Invalid or start time is greater than end time",
      })
    } else if (updatedData.selectedDays.length === 0) {
      notify.info({ message: "You need to select atleast one day" })
    } else if (!updatedData.text) {
      setError(true)
    } else onChange({ operationalHourSettings: updatedData })
  }

  const handleDaysSelect = (value) => {
    if (updatedData.enable) {
      if (updatedData?.selectedDays?.includes(value)) {
        setUpdatedData({
          ...updatedData,
          selectedDays: updatedData.selectedDays.filter(
            (item) => item !== value
          ),
        })
      } else {
        setUpdatedData({
          ...updatedData,
          selectedDays: [...updatedData.selectedDays, value],
        })
      }
    }
  }

  return (
    <Card
      style={{
        marginBottom: 16,
        backgroundColor: themeConfig.colorBgContainer,
      }}
    >
      <Notify />
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}>
            Operational Hours Settings
          </Title>
        </Col>
        <Col>
          <Space>
            <Text>Enable</Text>
            <Switch
              size="small"
              checked={updatedData?.enable}
              onChange={handleSwitch}
            />
          </Space>
        </Col>
      </Row>
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Row gutter={16}>
          <Col flex={1}>
            <Select
              size="large"
              style={{ width: "100%" }}
              placeholder="Select timezone"
              value={updatedData?.timezone}
              onChange={(value) => handleInputChange({ timezone: value })}
              disabled={!updatedData?.enable}
            >
              {Intl.supportedValuesOf("timeZone").map((timezone) => (
                <Select.Option key={timezone} value={timezone}>
                  {timezone}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col flex={1}>
            <TimePicker
              style={{ width: "100%" }}
              size="large"
              placeholder="Shift Start"
              disabled={!updatedData?.enable}
              value={dayjs(updatedData?.shiftStart)}
              format="HH:mm"
              onChange={(value) =>
                handleInputChange({
                  shiftStart: value ? value.valueOf() : null,
                })
              }
              onSelect={(value) => setTimeError(!value)}
            />
          </Col>
          <Col flex={1}>
            <TimePicker
              style={{ width: "100%" }}
              size="large"
              placeholder="Shift End"
              disabled={!updatedData?.enable}
              value={dayjs(updatedData?.shiftEnd)}
              format="HH:mm"
              onChange={(value) =>
                handleInputChange({ shiftEnd: value ? value.valueOf() : null })
              }
              onSelect={(value) => setTimeError(!value)}
            />
          </Col>
        </Row>
        <Text type="secondary">
          Note: Please take into consideration the time-zone of the live agent
          while setting the active hours.
        </Text>
        <div>
          <Title style={{ marginTop: 0 }} level={5}>
            Days
          </Title>
          <Space wrap>
            {DAYS.map((item) => (
              <Button
                key={item.value}
                type={
                  updatedData?.selectedDays?.includes(item.value)
                    ? "primary"
                    : "default"
                }
                style={{textTransform: "uppercase"}}
                disabled={!updatedData.enable}
                onClick={() => handleDaysSelect(item.value)}
              >
                {item.label}
              </Button>
            ))}
          </Space>
        </div>
        <div>
          <Title style={{ marginTop: 0 }} level={5}>
            Non operational hour message
          </Title>
          <Input.TextArea
            placeholder="Customer will see this message during non operational hour while requesting live chat"
            disabled={!updatedData?.enable}
            value={updatedData?.text}
            status={error ? "error" : ""}
            onChange={(e) => handleInputChange({ text: e.target.value })}
            rows={3}
          />
          {error && <Text type="danger">This field cannot be empty</Text>}
        </div>
        <Row justify="end">
          <Button
            type="primary"
            disabled={!updatedData?.enable}
            onClick={handleSave}
          >
            Save
          </Button>
        </Row>
      </Space>
    </Card>
  )
}

OperationalHourSettings.propTypes = {
  data: PropTypes.object,
  onChange: PropTypes.func.isRequired,
}

OperationalHourSettings.defaultProps = {
  data: {},
}

export default OperationalHourSettings
